import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Building2, ShieldCheck, Users, Landmark, Sparkles,
  Compass, Lightbulb, Handshake, Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, MaskedLines, stagger, item } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { PILLARS, CLIENTS, WHY_CHOOSE, MANIFESTO, IMAGES, SITE } from "@/lib/data";

const ICONS = {
  Building2, ShieldCheck, Users, Landmark, Sparkles,
  Compass, Lightbulb, Handshake, Heart,
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden bg-cream">
      {/* Parallax abstract accent, right side */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full lg:w-[46%] overflow-hidden"
      >
        <img
          src={IMAGES.heroAbstract}
          alt=""
          className="h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/40 to-transparent lg:from-cream lg:via-cream/10" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-6 pt-40 pb-24 lg:pt-52">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="overline mb-8"
        >
          Children's Services • Safeguarding • Service Transformation
        </motion.p>

        <h1 className="font-serif font-light tracking-tighter text-navy text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
          <MaskedLines
            lines={["Strengthening", "Children's Services"]}
          />
          <span className="block overflow-hidden">
            <motion.span
              className="block italic text-gold"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.95, delay: 0.39, ease: [0.22, 1, 0.36, 1] }}
            >
              Through Leadership,
            </motion.span>
          </span>
          <MaskedLines lines={["Partnership & Innovation"]} delayStart={0.51} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 max-w-xl text-lg leading-relaxed text-slate-600"
        >
          Epic Consultancy partners with governments, public sector organisations and
          children's services providers to strengthen safeguarding, improve service quality
          and create better outcomes for children and young people.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Button asChild className="rounded-full bg-navy hover:bg-navy-light text-cream px-8 h-12 text-sm">
            <Link to="/contact" data-testid="hero-book-cta">
              Book a Consultation <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="rounded-full border-navy/25 text-navy hover:bg-navy hover:text-cream px-8 h-12 text-sm"
          >
            <Link to="/expertise" data-testid="hero-expertise-cta">Explore Our Expertise</Link>
          </Button>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[0.65rem] tracking-[0.25em] uppercase text-navy/40">Scroll</span>
        <div className="h-10 w-px bg-navy/20 overflow-hidden">
          <motion.div
            className="h-4 w-px bg-gold"
            animate={{ y: [0, 24, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />

      {/* Editorial marquee */}
      <div className="border-y border-navy/10 py-10 bg-cream">
        <Marquee words={["Leadership", "Partnership", "Innovation", "Excellence", "Safeguarding"]} />
      </div>

      {/* Intro / positioning */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:py-36">
        <div className="grid gap-14 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline mb-6">Our Purpose</p>
              <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight leading-tight text-navy">
                We help organisations create safer, stronger and more effective children's services.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <p className="text-lg leading-relaxed text-slate-600">
                Rather than delivering isolated projects, Epic Consultancy works in partnership
                with organisations to design, develop, strengthen and transform services that
                improve outcomes for children and young people.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Expertise pillars */}
      <section className="bg-white border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 py-28">
          <Reveal className="mb-16 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="overline mb-4">Our Expertise</p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-navy max-w-2xl">
                Five strategic pillars
              </h2>
            </div>
            <Link
              to="/expertise"
              data-testid="expertise-view-all"
              className="group flex items-center gap-2 text-sm font-medium text-navy link-underline"
            >
              View full expertise <ArrowUpRight size={16} className="text-gold" />
            </Link>
          </Reveal>

          <div className="divide-y divide-slate-200/80 border-t border-slate-200/80">
            {PILLARS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <Reveal key={p.id} delay={i * 0.05}>
                  <Link
                    to={`/expertise#${p.id}`}
                    data-testid={`pillar-${p.id}`}
                    className="group grid grid-cols-12 items-center gap-4 py-8 transition-colors duration-300 hover:bg-cream/60"
                  >
                    <span className="col-span-2 md:col-span-1 font-serif text-2xl text-gold">{p.number}</span>
                    <div className="col-span-2 md:col-span-1 text-navy">
                      <Icon size={26} strokeWidth={1.5} />
                    </div>
                    <h3 className="col-span-8 md:col-span-5 font-serif text-xl md:text-2xl text-navy">
                      {p.title}
                    </h3>
                    <p className="col-span-10 col-start-3 md:col-span-4 md:col-start-8 text-sm text-slate-500 leading-relaxed">
                      {p.summary}
                    </p>
                    <span className="hidden md:flex md:col-span-1 justify-end">
                      <ArrowRight
                        size={20}
                        className="text-navy/40 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-gold"
                      />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who we work with */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="overline mb-4">Who We Work With</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-navy">
                Trusted by organisations shaping children's futures.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                From governments and ministries to charities and new providers — we partner with
                those working to improve the lives of children and young people.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="flex flex-wrap gap-3"
            >
              {CLIENTS.map((c) => (
                <motion.span
                  key={c}
                  variants={item}
                  data-testid="client-chip"
                  className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-navy/80 transition-colors duration-300 hover:border-gold hover:text-navy"
                >
                  {c}
                </motion.span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why choose — bento */}
      <section className="bg-navy text-cream grain">
        <div className="relative mx-auto max-w-7xl px-6 py-28">
          <Reveal className="mb-16 max-w-2xl">
            <p className="overline mb-4">Why Choose Epic Consultancy</p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-cream">
              Boutique expertise. Lasting impact.
            </h2>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-3">
            {WHY_CHOOSE.map((c, i) => {
              const Icon = ICONS[c.icon];
              const span = i === 0 ? "md:col-span-2" : i === 3 ? "md:col-span-2" : "md:col-span-1";
              return (
                <Reveal
                  key={c.title}
                  delay={i * 0.08}
                  className={span}
                >
                  <div
                    data-testid={`why-card-${i}`}
                    className="group h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-colors duration-500 hover:border-gold/50 hover:bg-white/[0.07]"
                  >
                    <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-gold/15 text-gold">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif text-2xl text-cream">{c.title}</h3>
                    <p className="mt-3 text-cream/70 leading-relaxed">{c.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Manifesto chapters */}
      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal className="mb-16">
          <p className="overline mb-4">Our Approach</p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-navy max-w-2xl">
            A partnership philosophy
          </h2>
        </Reveal>
        <div className="space-y-4">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.number} delay={i * 0.05}>
              <div className="group grid grid-cols-12 gap-6 border-t border-slate-200 py-10">
                <span className="col-span-12 md:col-span-3 font-serif text-6xl md:text-7xl text-slate-200 transition-colors duration-500 group-hover:text-gold/40">
                  {m.number}
                </span>
                <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl text-navy">
                  {m.title}
                </h3>
                <p className="col-span-12 md:col-span-5 text-lg text-slate-600 leading-relaxed">
                  {m.text}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pocket Keyworker teaser */}
      <section className="bg-teal text-cream">
        <div className="mx-auto max-w-7xl px-6 py-28 grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="overline mb-4">Digital Innovation</p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-cream">
              Pocket Keyworker
            </h2>
            <p className="mt-6 max-w-md text-cream/80 leading-relaxed text-lg">
              A digital companion supporting young people to build life skills, wellbeing and
              independence — created by Charmaine Laville to bring innovation into children's services.
            </p>
            <Button
              asChild
              className="mt-8 rounded-full bg-gold text-navy hover:bg-gold-bright px-8 h-12"
            >
              <Link to="/pocket-keyworker" data-testid="keyworker-teaser-cta">
                Discover Pocket Keyworker <ArrowRight size={16} className="ml-2" />
              </Link>
            </Button>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl overflow-hidden soft-shadow">
              <img src={IMAGES.keyworker} alt="Pocket Keyworker" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <Reveal className="rounded-[2rem] bg-navy px-8 py-20 md:px-20 text-center soft-shadow">
          <p className="overline mb-6">Start a Conversation</p>
          <h2 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-cream max-w-3xl mx-auto leading-tight">
            Let's strengthen your children's services, together.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild className="rounded-full bg-gold text-navy hover:bg-gold-bright px-8 h-12">
              <Link to="/contact" data-testid="cta-book">Book a Consultation</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-full border-cream/30 text-cream hover:bg-cream hover:text-navy px-8 h-12"
            >
              <a href={`mailto:${SITE.email}`} data-testid="cta-email">Email Charmaine</a>
            </Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
